/**
 * The site's own visual pieces, shared so the sign-in pages and the dashboard
 * look like the rest of Voilacode rather than a stock admin kit: the logo, the
 * header's type, the light/dark pill and the drifting-dust backdrop from the
 * About page.
 */
import * as React from 'react';
import { useTheme } from '@bloomneo/uikit';
import { createAboutField } from '@/features/main/components/aboutField';

/** The header's display type: condensed, uppercase, tracked out. */
export const headerFont = 'font-display font-bold uppercase tracking-[.08em]';
/** The small bracketed labels used across the site: [ THE TEAM ]. */
export const eyebrow = 'font-mono text-[11px] tracking-[.14em]';
/** The violet call-to-action pill. */
export const cta = 'rounded-full bg-brand font-semibold text-white transition-colors hover:bg-brand-hover';

/** The Voilacode mark: `</>` on a violet disc. */
export function Mark({ className = 'size-7' }: { className?: string }) {
  return (
    <svg viewBox="36 36 48 48" aria-hidden className={`${className} shrink-0`}>
      <circle cx="60" cy="60" r="24" fill="#5E19EC" />
      <path
        d="M53 52l-8 8 8 8M67 52l8 8-8 8M62 49l-4 22"
        stroke="#fff"
        strokeWidth="3.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The animated Voilacode logo, from the six-frame brand sheet. Animated WebP
 * with a real alpha channel, so the soft edges sit cleanly on either theme.
 *
 *   AnimatedMark  the V and `</>` only, for the header beside the name;
 *                 white on the dark theme, brand purple on the light one
 *   Lockup        mark, VOILACODE, TECHNOLOGIES and the strapline, for the
 *                 footer; the dark theme gets a copy with light lettering
 */
export function AnimatedMark({ className = 'h-9' }: { className?: string }) {
  const { mode } = useTheme();
  return (
    <img
      src={`/media/brand/logo-mark${mode === 'dark' ? '-white' : ''}.webp`}
      alt=""
      aria-hidden
      width={342}
      height={197}
      className={`${className} w-auto shrink-0`}
    />
  );
}

export function Lockup({ className = 'h-28' }: { className?: string }) {
  const { mode } = useTheme();
  return (
    <img
      src={`/media/brand/logo-lockup-${mode === 'dark' ? 'dark' : 'light'}.webp`}
      alt="Voilacode Technologies — Building tomorrow, together"
      width={449}
      height={328}
      className={`${className} w-auto shrink-0`}
    />
  );
}

/** The header's light/dark switch. */
export function ThemePill({ compact }: { compact?: boolean }) {
  const { mode, setMode } = useTheme();
  return (
    <button
      type="button"
      aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
      className={`${headerFont} flex h-10 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-[15px] transition-colors hover:border-primary`}
    >
      <span aria-hidden className="text-[15px]">{mode === 'dark' ? '☀' : '☾'}</span>
      {!compact && <span className="max-[419px]:hidden">{mode === 'dark' ? 'Light' : 'Dark'}</span>}
    </button>
  );
}

/**
 * The About page's dust field and a violet glow, fixed behind the content.
 * Purely decorative: no pointer events, hidden from assistive tech.
 */
export function Backdrop() {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  React.useEffect(() => {
    if (!canvasRef.current) return;
    const field = createAboutField({ canvas: canvasRef.current });
    return () => field.dispose();
  }, []);
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
      <canvas ref={canvasRef} className="block h-full w-full" />
      <div className="absolute -left-40 top-[-10%] size-[620px] rounded-full bg-brand/20 blur-[140px]" />
      <div className="absolute -right-40 bottom-[-20%] size-[520px] rounded-full bg-brand/15 blur-[140px]" />
    </div>
  );
}

/** A dashboard page's title, set like the site's section headings. */
export function PageTitle({ label, title, description, actions }: { label: string; title: string; description?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-5">
      <div className="flex min-w-0 flex-col gap-3">
        <div className={`${eyebrow} text-primary`}>[ {label} ]</div>
        <h1 className="m-0 font-display text-[clamp(44px,5.4vw,76px)] font-black uppercase leading-[.86] tracking-normal">{title}</h1>
        {description && <p className="m-0 max-w-[560px] text-[15px] leading-[1.6] text-soft">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2.5">{actions}</div>}
    </div>
  );
}

/** A glass panel, as the site uses for cards. */
export const panel = 'rounded-[22px] border border-line bg-glass backdrop-blur-[12px]';
